import Image from "next/image";

/** 이론·코딩 PDF의 공통 상품 설명과 데모. */
export default function SummaryPdfDescription({ theory, coding }: { theory: boolean; coding: boolean }) {
  const theoryContents = (
    <div className="bg-blue-50 dark:bg-blue-950/40 p-6 rounded-lg border-l-4 border-blue-500">
      <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-4">
        📚 이론 PDF 구성 내용
      </h3>
      <ul className="space-y-2 text-muted-foreground">
        <li className="flex items-start">
          <span className="text-blue-500 mr-2">•</span>
          <span>기출문제에 나온 키워드를 반영한 요약 PDF</span>
        </li>
        <li className="flex items-start">
          <span className="text-blue-500 mr-2">•</span>
          <span>2026년 2회까지의 기출문제 반영 완료</span>
        </li>
        <li className="flex items-start">
          <span className="text-blue-500 mr-2">•</span>
          <span>
            기출문제 분석으로 핵심 중에 핵심만 모은 압축 요약 PDF
          </span>
        </li>
      </ul>
    </div>
  );

  const codingContents = (
    <div className="bg-blue-50 dark:bg-blue-950/40 p-6 rounded-lg border-l-4 border-blue-500">
      <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-4">
        📚 코딩 PDF 구성 내용
      </h3>
      <ul className="space-y-2 text-muted-foreground">
        <li className="flex items-start">
          <span className="text-blue-500 mr-2">•</span>
          <span>기출문제에 나온 C언어, Java, Python, SQL 코드 패턴 정리</span>
        </li>
        <li className="flex items-start">
          <span className="text-blue-500 mr-2">•</span>
          <span>2026년 2회까지의 기출 코딩 문제 반영 완료</span>
        </li>
        <li className="flex items-start">
          <span className="text-blue-500 mr-2">•</span>
          <span>
            자주 출제되는 핵심 코드 유형을 압축 정리한 요약 PDF
          </span>
        </li>
      </ul>
    </div>
  );

  const featuresBlock = (
    <div className="bg-green-50 dark:bg-green-950/40 p-6 rounded-lg border-l-4 border-green-500">
      <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-4">
        🎉 두 PDF의 공통 기능
      </h3>
      <div className="space-y-4">
        <div>
          <h4 className="font-semibold text-foreground mb-2">
            1. 손쉽게 PDF 내용을 외웠는지 확인할 수 있는 가림막
            추가 PDF 제공
          </h4>
          <Image
            src="/summary-pdf/26-1-compressed/3d_compressed-with-djc_hide_gamja.webp"
            alt="가림막 감자 캐릭터"
            width={120}
            height={120}
            className="w-[80px] h-auto mx-auto my-2"
          />
          <p className="text-muted-foreground text-sm leading-relaxed">
            Q버전은 시험에 출제되는 주요 키워드가 가려져
            있어서 요약 PDF를 제대로 공부했는지 확인할 수
            있습니다. 가림막을 클릭하면 키워드가 보이며 편하게
            공부할 수 있습니다. <strong>v1-0 Q버전도 제공됩니다.</strong>
          </p>

          <Image
            src="/summary-pdf/25-3-compressed/q-version.gif"
            alt="가림막 버전 데모"
            width={500}
            height={400}
            className="w-[400px] h-auto mx-auto my-4"
          />
          <div className="bg-amber-50 dark:bg-amber-950/40 border border-yellow-200 rounded-lg p-3 my-3">
            <p className="text-amber-800 dark:text-amber-200 text-xs font-medium mb-1">
              ⚠️ 주의사항
            </p>
            <ul className="text-amber-800 dark:text-amber-200 text-xs space-y-1">
              <li>
                •{" "}
                <a
                  href="https://get.adobe.com/reader/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Adobe Acrobat Reader (클릭 시 다운로드)
                </a>{" "}
                데스크톱 앱을 권장합니다.
                <br />
                (Chrome 등 브라우저에서는 뷰어에 따라 동작이 달라질 수 있습니다.)
              </li>
              <li>
                • 맥 미리보기·모바일 PDF 앱에서는 가림막이 열리지 않습니다. 태블릿·모바일에서는 구매 목록의 “웹에서 보기”를 이용해 주세요.
              </li>
            </ul>
          </div>
        </div>
        <hr className="my-4" />
        <div>
          <h4 className="font-semibold text-foreground mb-2">
            2. 태블릿 유저를 위한 가로 버전 제공
          </h4>
          <p className="text-muted-foreground text-sm leading-relaxed">
            H버전은 태블릿 유저를 위한 가로 버전으로
            제공됩니다. 가로 버전은 세로버전에 비해 느슨한
            레이아웃으로 제공되어 필기에 적합합니다. <strong>v1-0 H버전도 제공됩니다.</strong>
          </p>
          <Image
            src="/summary-pdf/26-3-compressed/v1-0/8e5b8b63/h.webp"
            alt="가로 버전 데모"
            width={800}
            height={568}
            className="w-[800px] h-auto mx-auto my-4"
          />
        </div>
        <hr className="my-4" />
        <div>
          <h4 className="font-semibold text-foreground mb-2">
            3. 막히는 즉시 해소! &apos;바로가기&apos; 하이퍼링크
            🔗➡🥔 기능 탑재
          </h4>
          <p className="text-muted-foreground text-sm leading-relaxed">
            더 이상 요약본을 보다가 이해 안 되는 부분을 따로
            검색할 필요가 없습니다. PDF에서 궁금한 키워드 바로 옆
            하이퍼링크를 클릭하면 1초 만에 &apos;정처기
            감자&apos;의 이론·코딩 설명 페이지로 이동하여 학습하고
            문제를 풀 수 있습니다.
          </p>
          <Image
            src="/summary-pdf/25-3-compressed/output1.gif"
            alt="하이퍼링크 데모"
            width={500}
            height={258}
            className="w-[500px] h-auto mx-auto my-4"
          />
        </div>
        <hr className="my-4" />
        <div>
          <h4 className="font-semibold text-foreground mb-2">
            4. 한 번의 클릭으로 원하는 곳 이동! PDF 책갈피 기능
            추가
          </h4>
          <p className="text-muted-foreground text-sm leading-relaxed">
            PDF 뷰어(Acrobat Reader, 크롬 등) 패널에 PDF의 상세
            목차가 구성되어, 더 이상 페이지를 넘기거나 스크롤할
            필요 없이 원하는 챕터나 소제목으로 단 한 번의
            클릭만으로 이동할 수 있습니다.
          </p>
          <Image
            src="/summary-pdf/25-3-compressed/output2.gif"
            alt="책갈피 데모"
            width={600}
            height={536}
            className="w-[500px] h-auto mx-auto my-4"
          />
        </div>
      </div>
    </div>
  );

  const downloadBlock = (
    <div className="bg-muted p-6 rounded-lg border-l-4 border-gray-400">
      <h3 className="text-lg font-semibold text-foreground mb-4">
        📥 업데이트된 PDF 다운로드 방법
      </h3>
      <p className="text-muted-foreground text-sm leading-relaxed mb-2">
        <strong>
          내 메뉴(/my) {">"} 구매 목록 {">"} 다운로드 버튼
        </strong>
        을 클릭하면 최신 버전이 다운로드됩니다.
      </p>
      <p className="text-muted-foreground text-sm leading-relaxed">
        <strong>버전별로 다운 가능</strong> → 최신 버전이 마음에
        들지 않는다면 다시 기존 버전을 재 다운로드 받을 수
        있습니다.
      </p>
      <Image
        src="/summary-pdf/25-3-compressed/download.webp"
        alt="다운로드 데모"
        width={800}
        height={538}
        className="w-[500px] h-auto mx-auto my-4"
      />
    </div>
  );

  return <section className="space-y-6" aria-labelledby="pdf-description">
    <h2 id="pdf-description" className="text-2xl font-bold">상품 설명</h2>
    <p className="leading-relaxed text-muted-foreground">기출문제의 핵심 키워드를 정리한 이론 요약 PDF와, 기출 코딩 문제의 핵심 코드 패턴을 정리한 코딩 요약 PDF입니다. 이론 개념부터 C언어·Java·Python·SQL까지 함께 복습할 수 있습니다.</p>
    <p className="text-sm text-muted-foreground">아래 구성과 예시는 2026년 3회 상품 기준입니다.</p>
    {theory && theoryContents}
    {coding && codingContents}
    {featuresBlock}
    {downloadBlock}
  </section>;
}
